import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useActivity, useMeDetail } from "@/lib/api";

import { DeleteAccountDialog } from "./delete-account-dialog";
import { PasswordForm } from "./password-form";
import { ProfileForm } from "./profile-form";

export function ProfilePage() {
  const profileQuery = useMeDetail();
  const activityQuery = useActivity();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const profile = profileQuery.data;
  const activity = activityQuery.data ?? [];
  const isLoading = profileQuery.isLoading;
  const hasError = profileQuery.isError || activityQuery.isError;

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border flex items-center justify-between border-b px-6 py-4">
        <h1 className="font-heading text-primary text-xl font-bold">Profile</h1>
        <Link to="/history">
          <Button variant="outline">Back</Button>
        </Link>
      </header>
      <main className="mx-auto max-w-2xl space-y-6 px-6 py-8">
        {isLoading ? (
          <Card>
            <CardContent className="flex justify-center py-12">
              <Loader2 className="text-muted h-6 w-6 animate-spin" />
            </CardContent>
          </Card>
        ) : hasError || !profile ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted text-sm">
                Could not load profile data. Please{" "}
                <button
                  type="button"
                  onClick={() => {
                    void profileQuery.refetch();
                    void activityQuery.refetch();
                  }}
                  className="underline"
                >
                  try again
                </button>{" "}
                or{" "}
                <Link to="/history" className="underline">
                  go back
                </Link>
                .
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Account Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm">
                  <span className="text-muted">Email:</span> {profile.email}
                </p>
                <p className="text-sm">
                  <span className="text-muted">Role:</span> {profile.role}
                </p>
                <p className="text-sm">
                  <span className="text-muted">Member since:</span>{" "}
                  {new Date(profile.created_at).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>

            <ProfileForm
              initialFirstName={profile.first_name ?? ""}
              initialLastName={profile.last_name ?? ""}
            />

            <PasswordForm />

            <Card>
              <CardHeader>
                <CardTitle>Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                {activity.length === 0 ? (
                  <p className="text-muted text-sm">No activity yet.</p>
                ) : (
                  <div className="space-y-2">
                    {activity.map((entry) => (
                      <div
                        key={entry.id}
                        className="border-border flex items-start justify-between border-b pb-2 last:border-0"
                      >
                        <div>
                          <p className="text-sm font-medium">{entry.action}</p>
                          {entry.detail && <p className="text-muted text-xs">{entry.detail}</p>}
                        </div>
                        <p className="text-muted text-xs whitespace-nowrap">
                          {new Date(entry.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-destructive/40">
              <CardHeader>
                <CardTitle className="text-destructive">Danger zone</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted text-sm">
                  Deleting your account permanently erases it and every assessment you own (each
                  one’s report, expert calibration, share links, and invites). This cannot be
                  undone.
                </p>
                <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
                  Delete my account
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </main>
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
    </div>
  );
}
